/** 持久后台执行详情；关闭抽屉只收起界面，取消与重试是显式服务端操作。 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  landingProjectQueryKey,
  projectExecutionActive,
  useLandingProjectExecution,
} from '~/data/queries';
import { landingProjectsApi } from '~/hooks/use-landing-projects';
import { useAuthStore } from '~/stores/auth-store';
import { Drawer } from '~/ui/Dialog';
import { StatusChip } from '~/ui/StatusChip';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import { LandingAiCallDetails, LandingAiUsage } from './LandingAiUsage';
import { LandingCollapsibleNote } from './landing-collapsible-note';
import {
  PROJECT_RETRY_NOTE,
  ProjectProgressSummary,
  ProjectState,
  projectPhaseLabel,
} from './landing-project-display';
function ExecutionReviewResult({
  result,
  label = '内容审核',
}: {
  result: Record<string, unknown> | null;
  label?: string;
}) {
  if (!result) return null;
  if (result.skipped === true)
    return (
      <p className="muted">
        {typeof result.skip_reason === 'string' ? result.skip_reason : '已跳过'}
      </p>
    );
  const review = result.review_result;
  const findings: { quote: string; category: string }[] = [];
  if (review && typeof review === 'object') {
    for (const [key, field] of [
      ['illegal_words', 'word'],
      ['illegal_phrases', 'phrase'],
    ] as const) {
      const entries = Reflect.get(review, key);
      if (!Array.isArray(entries)) continue;
      for (const entry of entries) {
        if (!entry || typeof entry !== 'object') continue;
        const quote = Reflect.get(entry, field);
        const category = Reflect.get(entry, 'illegal_type');
        if (typeof quote === 'string' && typeof category === 'string')
          findings.push({ quote, category });
      }
    }
  }
  const boundary = Array.isArray(result.boundary_chapter_ids);
  if (!boundary && !review) return null;
  return (
    <div className="vstack" style={{ gap: 8, marginTop: 8 }}>
      <StatusChip
        tone={findings.length ? 'warn' : 'default'}
        label={`${boundary ? '拼接边界审核' : label} · ${findings.length ? `发现 ${findings.length} 项问题` : review ? '未发现问题' : '尚无审核结论'}`}
      />
      {result.needs_manual_resolution === true && (
        <p role="alert" style={{ color: 'var(--warn)' }}>
          待人工处理：请检查以下原文引用，调整章节或处理正文后重新审核拼接边界。
        </p>
      )}
      {findings.map((finding, index) => (
        <div key={`${index}-${finding.quote}`}>
          <span className="muted">{finding.category}</span>
          <blockquote
            dir="auto"
            style={{ margin: '4px 0', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
          >
            {finding.quote}
          </blockquote>
        </div>
      ))}
    </div>
  );
}
export function LandingProjectExecutionDrawer({
  projectId,
  executionId,
  onClose,
}: { projectId: string; executionId: string; onClose: () => void }) {
  const role = useAuthStore((state) => state.user?.role);
  const canProcess = role === 'root' || role === 'admin' || role === 'operator';
  const query = useLandingProjectExecution(projectId, executionId);
  const client = useQueryClient();
  const action = useMutation({
    mutationFn: (kind: 'retry' | 'cancel') =>
      kind === 'retry'
        ? landingProjectsApi.retry(projectId, executionId)
        : landingProjectsApi.cancel(projectId, executionId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: landingProjectQueryKey });
      toast('操作已提交，后台继续处理', { tone: 'info' });
    },
    onError: (error: Error) => toast(error.message, { tone: 'neg' }),
  });
  const isAi = query.data?.operation === 'review' || query.data?.operation === 'desensitize';
  return (
    <Drawer
      title={
        query.data?.operation === 'review'
          ? 'AI 审核进度与结果'
          : query.data?.operation === 'desensitize'
            ? 'AI 脱敏进度与结果'
            : '后台执行进度与结果'
      }
      subtitle={
        query.data?.operation === 'review'
          ? '审核检查营销正文；规则预处理独立留存，AI 审核不改写正文。关闭页面不影响后台执行。'
          : query.data?.operation === 'desensitize'
            ? '按需先审核，再脱敏；完成后不自动复审。关闭页面不影响后台执行。'
            : '关闭页面或刷新不会取消已提交的执行'
      }
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            关闭
          </button>
          {query.data?.can_retry && (
            <button
              type="button"
              className="btn btn-accent"
              disabled={action.isPending || (isAi && !canProcess)}
              onClick={() => action.mutate('retry')}
            >
              仅重试失败项
            </button>
          )}
          {projectExecutionActive(query.data) && (
            <button
              type="button"
              className="btn"
              disabled={action.isPending || (isAi && !canProcess)}
              onClick={() => action.mutate('cancel')}
            >
              取消本次请求
            </button>
          )}
        </>
      }
    >
      <div className="vstack" style={{ padding: 18, gap: 16 }}>
        {query.isPending ? (
          <p>加载执行详情…</p>
        ) : query.isError ? (
          <p role="alert">
            {query.error.message}
            <button type="button" className="btn" onClick={() => void query.refetch()}>
              重新加载
            </button>
          </p>
        ) : (
          query.data && (
            <>
              <ProjectProgressSummary execution={query.data} />
              {isAi && <LandingAiUsage usage={query.data.ai_usage} />}
              <Table
                className="landing-content-table"
                style={{ minWidth: 620, tableLayout: 'fixed' }}
              >
                <colgroup>
                  <col style={{ width: 170 }} />
                  <col style={{ width: 150 }} />
                  <col style={{ width: 300 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>章节 / 素材</th>
                    <th>阶段与完成量</th>
                    <th>结果 / 失败原因</th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.items?.map((item) => (
                    <tr key={item.id}>
                      {/* 新执行由服务端快照「第 N 章 · 标题」；仅早期记录缺 label，不再拿 UUID 片段冒充章号。 */}
                      <td
                        style={{ overflowWrap: 'anywhere' }}
                        title={
                          item.label ? undefined : (item.chapter_id ?? item.task_id ?? item.id)
                        }
                      >
                        {item.label ??
                          (Array.isArray(item.result?.boundary_chapter_ids)
                            ? '拼接边界'
                            : item.chapter_id
                              ? '章节（早期记录未保存章号）'
                              : item.task_id
                                ? '任务（早期记录未保存名称）'
                                : '执行子项')}
                      </td>
                      <td>
                        {projectPhaseLabel(item.phase)}
                        <div className="mono muted">
                          {item.completed}/{item.total}
                        </div>
                      </td>
                      <td>
                        <ProjectState status={item.status} />
                        {isAi && <LandingAiUsage usage={item.ai_usage} compact />}
                        {isAi && item.ai_usage && item.ai_usage.call_count > 0 && (
                          <LandingAiCallDetails
                            calls={item.model_calls ?? []}
                            total={item.ai_usage.call_count}
                          />
                        )}
                        <ExecutionReviewResult
                          result={item.result}
                          label={query.data.operation === 'desensitize' ? '脱敏前审核' : '内容审核'}
                        />
                        {item.error &&
                          (projectExecutionActive(query.data) &&
                          ['PENDING', 'RUNNING'].includes(item.status) ? (
                            // 可重试失败后子项回到 PENDING / RUNNING 并保留上次原因，不是终态报错。
                            <LandingCollapsibleNote
                              text={`${PROJECT_RETRY_NOTE}：${item.error}`}
                              summary={PROJECT_RETRY_NOTE}
                              label={item.label ? `${item.label} 上次失败原因` : undefined}
                            />
                          ) : (
                            <p
                              role="alert"
                              style={{ color: 'var(--neg)', overflowWrap: 'anywhere' }}
                            >
                              {item.error}
                            </p>
                          ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <p className="muted">
                审核发现内容问题属于审核结论；连接失败、模型拒绝或输出校验失败属于执行失败。已完成结果保留，重试不会重跑成功项。
              </p>
            </>
          )
        )}
      </div>
    </Drawer>
  );
}
